import RoundedButton from '@/components/ui/custom/buttons/RoundedButton';

import SecondaryInput from '@/components/ui/custom/secondary/SecondaryInput';
import SecondaryTextArea from '@/components/ui/custom/secondary/SecondaryTextArea';
import { ObjectiveInput } from '@/models/astro.ai/generate/Objective';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useMutation } from '@tanstack/react-query';
import { FormEventHandler } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Modal from '@/components/ui/custom/modals/Modal';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CanceledError } from 'axios';

export type GenerateObjectiveFunction = (
  args: GenerateObjectiveFields,
) => Promise<void>;

type ModalGenerateObjectiveProps = {
  onGenerate: GenerateObjectiveFunction;
  onClose: () => void;
  visible: boolean;
  hasValue?: boolean;
};

type GenerateObjectiveFields = Pick<ObjectiveInput, 'topic' | 'purpose'>;

export default function ModalGenerateObjective({
  onClose,
  visible,
  ...props
}: ModalGenerateObjectiveProps) {
  const { t: tModal } = useTranslation('translation', {
    keyPrefix: 'modalGenerateObjective',
  });

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      testId="modalGenerateObjective"
      className={{
        base: 'gap-3 flex flex-col p-6',
        icon: 'w-4 h-4',
      }}
    >
      <ConditionalRenderer condition={visible}>
        <Text
          format="rubik-500"
          className="text-primary text-18"
          text={tModal('title')}
        />

        <ModalGenerateObjectiveForm onClose={onClose} {...props} />
      </ConditionalRenderer>
    </Modal>
  );
}

const ModalGenerateObjectiveForm = ({
  onClose,
  onGenerate,
  hasValue,
}: Pick<
  ModalGenerateObjectiveProps,
  'onGenerate' | 'onClose' | 'hasValue'
>) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { t: tModal } = useTranslation('translation', {
    keyPrefix: 'modalGenerateObjective',
  });

  const { register, handleSubmit } = useForm<GenerateObjectiveFields>();

  const { mutate: generate, isPending } = useMutation(onGenerate, {
    onError: err => {
      if (err instanceof CanceledError) return;
      alert.error(getErrorMessage(err));
    },
    onSuccess: () => {
      onClose();
    },
  });

  const onSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(fields => generate(fields))(e);
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <SecondaryInput
        {...register('topic')}
        placeholder={tModal('form.topic.placeholder')}
        label={{
          text: tModal('form.topic.label'),
          position: 'outside',
        }}
      />
      <SecondaryTextArea
        {...register('purpose')}
        placeholder={tModal('form.purpose.placeholder')}
        label={{ text: tModal('form.purpose.label'), position: 'outside' }}
      />
      <ConditionalRenderer condition={hasValue}>
        <div
          role="alert"
          className="alert bg-warning-content text-warning font-500"
        >
          <ExclamationTriangleIcon className="w-6 h-6" />
          <Text
            className="text-16 text-left font-normal"
            text={tModal('hasValue')}
            color="warning"
          />
        </div>
      </ConditionalRenderer>

      <div className="flex w-full justify-around gap-6">
        <RoundedButton
          text={t('cancel')}
          color="neutral"
          className="w-full h-8 text-14 !font-rubik-500 !text-base-100 p-0 lg:p-0 md:p-0"
          type="button"
          onClick={onClose}
        />
        <RoundedButton
          text={tModal('generate')}
          className="w-full h-8 text-14 !font-rubik-500 !text-base-100 p-0 lg:p-0 md:p-0"
          type="submit"
          loading={isPending}
        />
      </div>
    </form>
  );
};
